/**
 * PlaylistEditToolbar — top-right cluster of three pills shown on each tile
 * in playlist edit mode:
 *
 *   [◀] [▶] [✕]   ← reorder + remove (✕ becomes ✓ on first click for confirm)
 *
 * Replaces the prior split rendering where `<PlaylistEdit>` (red ✕) and
 * `<PlaylistReorderControls>` (chevrons) lived as separate corner overlays.
 * Combining them into one toolbar makes the three controls read as a
 * single "tile edit" affordance, and lets the chevrons sit immediately
 * to the left of the remove button.
 *
 * Used by:
 *   - CommunityProfileCarousels (PlaylistTileGrid + PlaylistItemCarousel
 *     editMode branches)
 */

import React from 'react'
import { PlaylistReorderControls } from '../PlaylistReorderControls/index'
import { PlaylistRemoveConfirm } from '../PlaylistRemoveConfirm/index'

// Hoisted to module scope so the type doesn't get re-declared on every render.
type ReorderFn = (fromIndex: number, toIndex: number, draggedItemId: string) => void

export interface PlaylistEditToolbarProps {
  /** 0-based index of THIS tile within its playlist row. */
  index: number
  /** Total tiles in the row. */
  totalCount: number
  /** Stable id of THIS tile. */
  itemId: string
  /** Reorder handler.  Omit (or pass null) to hide the chevrons. */
  onReorder?: ((fromIndex: number, toIndex: number, itemId: string) => void) | null
  /** Remove handler.  Omit (or pass null) to hide the ✕/✓ button. */
  onRemove?: (() => void) | null
  /** Pill + icon size for all child controls.  'small' (default) is the
   *  carousel-row size; 'medium' is for tablet-width SystemPlaylistFullView;
   *  'large' is for desktop SystemPlaylistFullView. */
  size?: 'small' | 'medium' | 'large'
  /** When true, disable both reorder chevrons (independent of position).
   *  Used by the parent to lock clicks while a move is in flight. */
  reorderDisabled?: boolean
  /** Localized aria-label / tooltip for the "earlier" chevron. */
  earlierLabel?: string
  /** Localized aria-label / tooltip for the "later" chevron. */
  laterLabel?: string
  /** Localized aria-label / tooltip for the idle remove button. */
  removeLabel?: string
  /** Localized aria-label / tooltip for the confirming remove button. */
  confirmLabel?: string
}

export function PlaylistEditToolbar ({
  index,
  totalCount,
  itemId,
  onReorder,
  onRemove,
  size = 'small',
  reorderDisabled = false,
  earlierLabel,
  laterLabel,
  removeLabel,
  confirmLabel,
}: PlaylistEditToolbarProps) {
  if (!onReorder && !onRemove) return null

  // Reorder is only meaningful when there's more than one item to move
  // around.  Hiding (rather than disabling) the chevrons keeps the toolbar
  // tidy on single-item playlists.
  const showReorder = Boolean(onReorder) && totalCount > 1

  return (
    <div className={`playlist-edit-toolbar playlist-edit-toolbar--${size}`}>
      {showReorder ? (
        <PlaylistReorderControls
          index={index}
          totalCount={totalCount}
          itemId={itemId}
          onReorder={onReorder as ReorderFn}
          size={size}
          disabled={reorderDisabled}
          earlierLabel={earlierLabel}
          laterLabel={laterLabel}
        />
      ) : null}
      {onRemove ? (
        <PlaylistRemoveConfirm
          onRemove={onRemove}
          size={size}
          removeLabel={removeLabel}
          confirmLabel={confirmLabel}
        />
      ) : null}
    </div>
  )
}
